/**
 * Asset recovery at exit (AST-6) and asset handover at onboarding (AST-13) — the asset side of
 * both flows, as an API only. The exit clearance screen belongs to the Exit / Full & Final team and
 * the onboarding screen to Onboarding; they consume these routes. Its own router (distinct path
 * prefix, no overlap with `asset.contract.ts`'s `/asset/*` or Administration's
 * `/asset-management/*` tabs) for the same reason asset-batch/asset-history are.
 *
 * Exit recovery, in the order the clearance flow uses it:
 *   1. `listExitAssets`         — a person's open assignments with book value (financial read only).
 *   2. `openExitReturnRequests` — opens a return request on every open assignment at once.
 *   3. `resolveExitAsset`       — settles ONE assignment: returned, charged (Decimal amount +
 *                                 currency) or written off. A financial mutation, so it requires an
 *                                 `Idempotency-Key` header and a retried request returns the original
 *                                 result (CLAUDE.md invariant 10). The settlement is recorded in
 *                                 `asset_recovery_resolutions`; the F&F side reads `resolved` from
 *                                 `listExitAssets` to pick up charged amounts.
 *
 * Onboarding handover: `suggestOnboardingKit` (what people in the same designation / department
 * hold today, with stock on hand) and `assignOnboardingAsset` (assigns an AVAILABLE asset to the
 * new joiner, origin `direct`).
 *
 * Permissions: every route needs a real permission AND a scope check against the PERSON the asset
 * is going to / coming from — the declared list is the entry gate only.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import {
  currencySchema,
  dateOnlySchema,
  decimalStringSchema,
  errorBodySchema,
  idempotencyKeySchema,
  instantSchema,
  orgIdSchema,
  paginatedSchema,
  paginationQuerySchema,
  uuidSchema,
} from '../schemas/common.js';
import { routeMeta } from './meta.js';
import {
  assetAssignmentSchema,
  assetConditionSchema,
  assetStatusSchema,
  assetSummaryPersonSchema,
} from './asset.contract.js';

const c = initContract();

const idempotencyHeadersSchema = z.object({
  'idempotency-key': idempotencyKeySchema,
});

// ---------------------------------------------------------------------------
// Exit recovery — DTOs
// ---------------------------------------------------------------------------

export const assetRecoveryOutcomeSchema = z.enum(['returned', 'charged', 'written_off']);
export type AssetRecoveryOutcome = z.infer<typeof assetRecoveryOutcomeSchema>;

/** How an already-closed assignment was settled. Amounts are present only with `asset.financial:read`. */
export const assetRecoveryResolutionSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  assignmentId: uuidSchema,
  assetId: uuidSchema,
  assetCode: z.string(),
  assetName: z.string(),
  personId: uuidSchema,
  outcome: assetRecoveryOutcomeSchema,
  amount: decimalStringSchema.nullable().optional(),
  currency: currencySchema.nullable().optional(),
  bookValue: decimalStringSchema.nullable().optional(),
  note: z.string().nullable(),
  resolvedBy: assetSummaryPersonSchema.nullable(),
  resolvedAt: instantSchema,
});
export type AssetRecoveryResolution = z.infer<typeof assetRecoveryResolutionSchema>;

export const exitAssetItemSchema = z.object({
  assignmentId: uuidSchema,
  assetId: uuidSchema,
  assetCode: z.string(),
  assetName: z.string(),
  categoryName: z.string(),
  typeName: z.string().nullable(),
  serialNumber: z.string().nullable(),
  assetStatus: assetStatusSchema,
  condition: assetConditionSchema,
  assignedAt: instantSchema,
  acknowledgedAt: instantSchema.nullable(),
  returnRequestedAt: instantSchema.nullable(),
  /** Depreciated value today, in `bookValueCurrency`. Omitted without `asset.financial:read`; null when it cannot be computed (no purchase value). */
  bookValue: decimalStringSchema.nullable().optional(),
  bookValueCurrency: currencySchema.nullable().optional(),
});
export type ExitAssetItem = z.infer<typeof exitAssetItemSchema>;

export const exitAssetListSchema = z.object({
  personId: uuidSchema,
  /** True when `bookValue` / amounts are populated for this caller. */
  financialVisible: z.boolean(),
  open: z.array(exitAssetItemSchema),
  /** Assets of this person already settled through `resolveExitAsset`, newest first. */
  resolved: z.array(assetRecoveryResolutionSchema),
});
export type ExitAssetList = z.infer<typeof exitAssetListSchema>;

/** One open assignment of a person who is leaving, with who they are — the IT worklist row. */
export const activeExitAssetItemSchema = exitAssetItemSchema.extend({
  personId: uuidSchema,
  personName: z.string(),
  employeeCode: z.string().nullable(),
  offboardingProcessId: uuidSchema,
  lastWorkingDay: dateOnlySchema.nullable(),
});
export type ActiveExitAssetItem = z.infer<typeof activeExitAssetItemSchema>;

export const listActiveExitAssetsQuerySchema = paginationQuerySchema;

export const listActiveExitAssetsResultSchema = paginatedSchema(activeExitAssetItemSchema).extend({
  /** True when `bookValue` is populated for this caller (`asset.financial:read`). */
  financialVisible: z.boolean(),
});
export type ListActiveExitAssetsResult = z.infer<typeof listActiveExitAssetsResultSchema>;

// ---------------------------------------------------------------------------
// Exit recovery — requests
// ---------------------------------------------------------------------------

export const openExitReturnRequestsBodySchema = z.object({
  note: z.string().trim().max(500).optional(),
});
export type OpenExitReturnRequestsBody = z.infer<typeof openExitReturnRequestsBodySchema>;

export const openExitReturnRequestsResultSchema = z.object({
  personId: uuidSchema,
  /** Return requests opened by this call. */
  requested: z.number().int().nonnegative(),
  /** Open assignments that already had a return request — left untouched. */
  alreadyRequested: z.number().int().nonnegative(),
  assignmentIds: z.array(uuidSchema),
});
export type OpenExitReturnRequestsResult = z.infer<typeof openExitReturnRequestsResultSchema>;

const resolveNoteSchema = z.string().trim().max(500).optional();

export const resolveExitAssetBodySchema = z.discriminatedUnion('outcome', [
  z.object({
    outcome: z.literal('returned'),
    /** Condition the asset came back in. */
    condition: assetConditionSchema,
    note: resolveNoteSchema,
  }),
  z.object({
    outcome: z.literal('charged'),
    /** What the person owes for the asset, > 0. Decimal string, never a number. */
    amount: decimalStringSchema.refine((v) => Number.parseFloat(v) > 0, 'Must be greater than zero'),
    currency: currencySchema,
    note: resolveNoteSchema,
  }),
  z.object({
    outcome: z.literal('written_off'),
    /** Why the loss is absorbed — required so a write-off is always explained. */
    note: z.string().trim().min(3).max(500),
  }),
]);
export type ResolveExitAssetBody = z.infer<typeof resolveExitAssetBodySchema>;

// ---------------------------------------------------------------------------
// Onboarding handover — DTOs & requests
// ---------------------------------------------------------------------------

export const suggestOnboardingKitQuerySchema = z
  .object({
    designation: z.string().trim().min(1).max(200).optional(),
    departmentId: uuidSchema.optional(),
  })
  .refine((q) => q.designation !== undefined || q.departmentId !== undefined, {
    message: 'Provide a designation or a departmentId',
  });
export type SuggestOnboardingKitQuery = z.infer<typeof suggestOnboardingKitQuerySchema>;

export const kitSuggestedAssetSchema = z.object({
  id: uuidSchema,
  assetCode: z.string(),
  name: z.string(),
  condition: assetConditionSchema,
  locationName: z.string().nullable(),
});

export const kitSuggestionItemSchema = z.object({
  categoryId: uuidSchema,
  categoryName: z.string(),
  typeId: uuidSchema,
  typeName: z.string(),
  /** Peers (same designation / department) currently holding this type. */
  holderCount: z.number().int().nonnegative(),
  /** Whole-number percentage of peers holding it (holderCount / peerCount). */
  coveragePercent: z.number().int().min(0).max(100),
  availableCount: z.number().int().nonnegative(),
  /** A few in-scope available assets of this type, oldest first, ready to hand over. */
  suggestedAssets: z.array(kitSuggestedAssetSchema),
});

export const onboardingKitSuggestionSchema = z.object({
  designation: z.string().nullable(),
  departmentId: uuidSchema.nullable(),
  /** Active people the suggestion is based on. 0 means no data: `items` is empty, not "no kit". */
  peerCount: z.number().int().nonnegative(),
  items: z.array(kitSuggestionItemSchema),
});
export type OnboardingKitSuggestion = z.infer<typeof onboardingKitSuggestionSchema>;

export const assignOnboardingAssetBodySchema = z.object({
  assetId: uuidSchema,
  condition: assetConditionSchema.optional(),
  note: z.string().trim().max(500).optional(),
});
export type AssignOnboardingAssetBody = z.infer<typeof assignOnboardingAssetBodySchema>;

// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------

export const assetHandoverContract = c.router(
  {
    // Static/longer literal paths are declared before any `:param` sibling of the same length.
    suggestOnboardingKit: {
      method: 'GET',
      path: '/onboarding/kit-suggestion',
      query: suggestOnboardingKitQuerySchema,
      summary:
        'Suggest an onboarding asset kit from what people in the same designation/department hold (asset:assign)',
      metadata: routeMeta('asset:assign'),
      responses: {
        200: onboardingKitSuggestionSchema,
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
      },
    },
    assignOnboardingAsset: {
      method: 'POST',
      path: '/onboarding/:personId/assign',
      summary: 'Assign an available asset to a new joiner, origin direct (asset:assign)',
      pathParams: z.object({ personId: uuidSchema }),
      headers: idempotencyHeadersSchema,
      body: assignOnboardingAssetBodySchema,
      metadata: routeMeta('asset:assign'),
      responses: {
        200: assetAssignmentSchema,
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
    },
    listActiveExitAssets: {
      method: 'GET',
      path: '/exit/assets',
      summary:
        'List the open asset assignments of everyone with an active or on-hold offboarding process (asset:read)',
      query: listActiveExitAssetsQuerySchema,
      metadata: routeMeta('asset:read'),
      responses: {
        200: listActiveExitAssetsResultSchema,
        401: errorBodySchema,
        403: errorBodySchema,
      },
    },
    listExitAssets: {
      method: 'GET',
      path: '/exit/:personId/assets',
      summary: "List a leaving person's open asset assignments with book value (asset:read)",
      pathParams: z.object({ personId: uuidSchema }),
      metadata: routeMeta('asset:read'),
      responses: {
        200: exitAssetListSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
    },
    openExitReturnRequests: {
      method: 'POST',
      path: '/exit/:personId/return-requests',
      summary: 'Open return requests for all of a leaving person’s open assignments (asset:assign / asset:return)',
      pathParams: z.object({ personId: uuidSchema }),
      headers: idempotencyHeadersSchema,
      body: openExitReturnRequestsBodySchema,
      metadata: routeMeta(['asset:assign', 'asset:return']),
      responses: {
        200: openExitReturnRequestsResultSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
    },
    resolveExitAsset: {
      method: 'POST',
      path: '/exit/assignments/:assignmentId/resolve',
      summary:
        'Settle one asset for exit: returned, charged (Decimal amount + currency) or written off. Requires Idempotency-Key',
      pathParams: z.object({ assignmentId: uuidSchema }),
      headers: idempotencyHeadersSchema,
      body: resolveExitAssetBodySchema,
      // Per outcome, enforced by the handler: returned -> asset:return / asset:assign, written_off ->
      // asset:retire, charged -> asset:financial:manage + asset.financial:read (Finance, not IT).
      metadata: routeMeta([
        'asset:return',
        'asset:assign',
        'asset:retire',
        'asset:financial:manage',
      ]),
      responses: {
        200: assetRecoveryResolutionSchema,
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
    },
  },
  { pathPrefix: '/asset-management/handover' },
);
